import { useCallback, useEffect, useMemo } from "react";
import { SubmissionError } from "redux-form";
import { skipToken } from "@reduxjs/toolkit/query";

import { updateVisitsCounter } from "@js/apps/common/actions";
import { useGetEmployerPublicProfileQuery } from "@js/apps/employer/api";
import { useUserNeedsIdentityVerification } from "@js/apps/identity-verification";
import { useGetJobQuery } from "@js/apps/jobs/api";
import { Snackbar } from "@js/components/snackbar";
import { useNavigate } from "@js/hooks/";
import { useIdParam } from "@js/hooks/use-id-param";
import { parseFormSubmitError } from "@js/utils";

import {
  useAcceptJobOfferExtensionMutation,
  useAcceptJobOfferMutation,
  useGetFreelancerOfferQuery,
} from "../../api";
import { openVerifyIdentityToAcceptOfferModal } from "../../components/verify-id-to-accept-offer-modal";
import { openRejectOfferModal } from "../../forms";
import type { AcceptOfferFormData } from "../../forms/accept-offer-form";
import { offerRequiresExtensionAcceptance } from "../../utils";

export const useTalentOffer = () => {
  const navigate = useNavigate();
  const jobId = useIdParam();
  const offerId = useIdParam("offerId");
  const { userNeedsIdentityVerification } = useUserNeedsIdentityVerification();

  const [acceptJobOffer] = useAcceptJobOfferMutation();
  const [acceptJobOfferExtension] = useAcceptJobOfferExtensionMutation();
  const { data: offer, isLoading: isOfferLoading } = useGetFreelancerOfferQuery(
    offerId ?? skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const { data: job, isLoading: isJobLoading } = useGetJobQuery(
    jobId ? { id: jobId } : skipToken,
  );
  const { data: employerProfile, isFetching: fetchingEmployerPublicProfile } =
    useGetEmployerPublicProfileQuery(job?.employer.id ?? skipToken);

  useEffect(() => {
    if (!job || !offer) {
      return;
    }

    const isOfferMatchingJob = offer.job.id === job.id;
    if (!isOfferMatchingJob) {
      navigate("/page-not-found/", { replace: true });
    }
  }, [job, offer, navigate]);

  useEffect(() => {
    if (!offerId) {
      return;
    }

    void updateVisitsCounter(offerId, ENUMS.VisitsCounterType.OFFER);
  }, [offerId]);

  const onSubmit = async (values: AcceptOfferFormData) => {
    if (!offer) {
      return;
    }

    const acceptOfferCallback = async () => {
      if (offerRequiresExtensionAcceptance(offer)) {
        await acceptJobOfferExtension({
          id: offer.id,
          contract_terms_accepted_at: values.contract_terms_accepted_at,
        }).unwrap();
        navigate(`/jobs/`);

        return;
      }

      await acceptJobOffer({
        id: offer.id,
        checksum: offer.checksum,
        address: values.address,
        contract_terms_accepted_at: values.contract_terms_accepted_at,
      }).unwrap();
      navigate(`/jobs/${jobId}/offers/${offer?.id}/accepted/`);
    };

    if (userNeedsIdentityVerification && !offer.idv_acceptance_bypassed) {
      openVerifyIdentityToAcceptOfferModal({
        onVerificationFail: () => {
          navigate("/talent/dashboard/my_jobs/offers");
        },
        onVerificationSuccess: () => {
          acceptOfferCallback().catch((error) => {
            onSubmitFail(error);
          });
        },
      });
      return;
    }

    try {
      await acceptOfferCallback();
    } catch (error) {
      throw new SubmissionError(
        parseFormSubmitError({
          error,
          defaultGeneralFormError: "Something went wrong",
        }),
      );
    }
  };

  const onSubmitFail = (errors: Record<string, string>) => {
    if (errors._error) {
      Snackbar.error(errors._error);
    }
  };

  const initialValues = useMemo(() => {
    return {
      address: offer?.shipping_address,
    };
  }, [offer]);

  const onOfferReject = useCallback(() => {
    if (!offer) {
      return;
    }

    openRejectOfferModal({ offer });
  }, [offer]);

  const isLoading =
    isJobLoading || isOfferLoading || fetchingEmployerPublicProfile;

  return {
    loading: isLoading,
    offer,
    offerId: Number(offerId),
    job,
    employer: employerProfile,
    onOfferReject,
    onSubmit,
    onSubmitFail,
    initialValues,
  };
};
